<template>
  <div class="container">
    <p>
      Crea y lanza estrategias masivas para contactar con tus contactos.
      <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
    </p>
    <strategy-table-controls @update="handleUpdate" />
    <strategy-table :loading="loading" @update="handleUpdate" />
  </div>
</template>

<script>
import { ref, onMounted, provide, watch, onBeforeUnmount, computed, h } from "vue";
import { useStore } from "vuex";

import StrategyTable from "@/app/strategies/components/tables/StrategyTable.vue";
import StrategyTableControls from "@/app/strategies/components/tables/StrategyTableControls.vue";
import { StrategyTTSGenerationStatus } from "@/app/strategies/utils/modalStrategy";
import { Modal } from "ant-design-vue";
import { InfoCircleFilled } from "@ant-design/icons-vue";
import { useRoute } from "vue-router";
import { TABLE_PAGE_SIZE_REDUCE } from "@/app/shared/utils/constants";
import EventBus from "@/app/shared/utils/eventBus";

const TIME_TO_UPDATE = 5;
const REFRESH_STRATEGIES_AFTER_WHATSAPP_FINISH = "REFRESH_STRATEGIES_AFTER_WHATSAPP_FINISH";

export default {
  components: {
    StrategyTable,
    StrategyTableControls,
  },
  setup() {
    const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/operaciones/estrategias-1/`;

    const store = useStore();
    const route = useRoute();

    let updateIntervalId = null;
    const user_track = 1;

    const loading = ref(false);
    const filters = ref();
    const pagination = ref({
      current: 1,
      pageSize: TABLE_PAGE_SIZE_REDUCE,
      showSizeChanger: false,
      total: 0,
    });
    const countToUpdate = ref(TIME_TO_UPDATE);
    const isSomeStrategyRunning = computed(() =>
      store.getters["getStrategyInfo"]?.items?.some((strategy) => strategy?.turned_on)
    );

    const strategiesData = computed(() => store.getters["getStrategyInfo"]);

    const isSomeTtsGenerating = computed(() =>
      store.getters["getStrategyInfo"]?.items?.some((strategy) => {
        const ttsDetail = strategy?.tts_detail ?? strategy?.tts_details;
        return ttsDetail?.generation_status === StrategyTTSGenerationStatus.Generating;
      })
    );

    watch(countToUpdate, async () => {
      if (
        countToUpdate.value === 1 &&
        (isSomeStrategyRunning.value || isSomeTtsGenerating.value)
      ) {
        await fetchData({ showLoader: false });
      }
    });

    watch([isSomeStrategyRunning, isSomeTtsGenerating], () => {
      countToUpdate.value = TIME_TO_UPDATE;
    });

    const fetchData = async ({ showLoader = true } = {}) => {
      if (showLoader) loading.value = true;

      await store.dispatch("fetchAllStrategies", {
        filters: filters.value,
        pagination: pagination.value,
      });

      Object.assign(pagination.value, {
        current: pagination.value.current || 1,
        ...store.getters["getStrategyInfo"]?.pagination,
      });

      if (showLoader) loading.value = false;
    };

    const handleWhatsappFinishedRefresh = async () => {
      await fetchData({ showLoader: false });
    };

    provide("TIME_TO_UPDATE", TIME_TO_UPDATE);
    provide("filters", filters);
    provide("pagination", pagination);
    provide("countToUpdate", countToUpdate);
    provide("isSomeStrategyRunning", isSomeStrategyRunning);
    provide("fetchTableData", fetchData);

    const showStrategyInfoModal = (strategyName, equivalences_nc, automatic = false) => {
      const liElements = [
        h(
          'li',
          {
            style: { fontWeight: 500 },
          },
          'Añadir usuarios'
        ),
      ]

      // 👉 Solo agregar el segundo li si equivalences_nc está vacío
      const allEmpty =
        !equivalences_nc ||
        equivalences_nc.length === 0 ||
        equivalences_nc.every(
          (item) =>
            item == null ||
            item.result == null ||
            item.result === '' ||
            (Array.isArray(item.result) && item.result.length === 0)
        )
      if (allEmpty) {
        liElements.push(
          h(
            'li',
            {
              style: { fontWeight: 500 },
            },
            'Configurar el equivalente del tipo de grupo “No contacto” de tu campaña'
          )
        )
      }

      Modal.warning({
        title: 'Importante',
        width: 480,
        centered: true,
        content: h('div', { style: { marginTop: '8px' } }, [
          h(
            'p',
            {
              style: {
                color: '#595959',
                marginBottom: '8px',
                lineHeight: '1.5',
              },
            },
            `Tu estrategia predictiva “${strategyName}” ${automatic ? 'está programada' : 'está lista para que se inicie de forma manual'}, pero recuerda que no será iniciada hasta que configures los siguientes pasos:`
          ),
          h(
            'ul',
            {
              style: {
                color: '#434343',
                margin: '8px 0 16px 24px',
                padding: 0,
              },
            },
            liElements
          ),
        ]),
        okText: 'Entendido',
        onOk() {
          localStorage.removeItem('predictive_scheduled')
        },
      })
    };

    onMounted(async () => {
      EventBus.$on(REFRESH_STRATEGIES_AFTER_WHATSAPP_FINISH, handleWhatsappFinishedRefresh);

      if (user_track == localStorage.getItem("used_id")) {
        const url = new URL(window.location.href);
        url.searchParams.set("tce", "act");
        window.history.replaceState(null, "", url.href);
      }
      await fetchData();

      console.log(route.path, 'route in strategies template');
      if (localStorage.getItem("predictive_scheduled") && route.path == "/operations/strategies") {
        const scheduledInfo = JSON.parse(localStorage.getItem("predictive_scheduled"));
        const strategyLastRegister = strategiesData.value?.items?.find(
          (strategy) => strategy.id === scheduledInfo.id
        );
        console.log(strategyLastRegister, 'strategy last register');
        showStrategyInfoModal(scheduledInfo.name, strategyLastRegister?.equivalences_nc || [], strategyLastRegister.execution_time !== null)
      }

      updateIntervalId = setInterval(() => {
        countToUpdate.value = countToUpdate.value - 1;

        if (countToUpdate.value === 0) countToUpdate.value = TIME_TO_UPDATE;
      }, 1000);
    });

    onBeforeUnmount(() => {
      if (updateIntervalId) clearInterval(updateIntervalId);
      EventBus.$off(REFRESH_STRATEGIES_AFTER_WHATSAPP_FINISH, handleWhatsappFinishedRefresh);
    });

    const handleUpdate = async () => {
      await fetchData();
      countToUpdate.value = TIME_TO_UPDATE;
    };

    watch(filters, async () => {
      pagination.value.current = 1;
      await fetchData();
    });
    watch(pagination, async () => await fetchData());

    watch(isSomeStrategyRunning, () => {
      countToUpdate.value = TIME_TO_UPDATE;
    });

    return { loading, fetchData, handleUpdate, DOCS_URL };
  },
  sockets: {
    reloadStrategy() {
      this.fetchData({ showLoader: false });
    },
  },
};
</script>

<style lang="sass" scoped>
.container
  padding: 12px 20px

.controls
  margin-bottom: 8px
</style>